import { FramedList, FramedListRow } from '@kerfjs/ui/framed-list';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Row } from '@kerfjs/ui/row';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';
import { Info, ScanSearch, TriangleAlert } from 'lucide';

import type { ReviewFindingT } from '../../../../shared/contract/analysis.js';
import { reviewFindingMeta } from '../../../../shared/quotation-review.js';
import type { QuoteMatrix } from '../../../data/api.js';
import type { IntakeLine } from '../../../data/state.js';
import { CurrencyAmount } from '../../currency-amount.js';

export function EvidenceQuotes({
  line,
  matrix,
  base,
  reviewFindings = [],
  compact = false,
}: {
  line: IntakeLine;
  matrix: QuoteMatrix | null;
  base: string;
  reviewFindings?: ReviewFindingT[];
  compact?: boolean;
}): SafeHtml {
  const columns = matrix?.columns ?? [];
  return (
    <List gap="xs">
      {columns.length === 0 ? (
        <Text tone="quiet">No quotations recorded.</Text>
      ) : compact ? (
        <FramedList label="Vendor quotes">
          {columns.map((column) => {
            const cell = matrix?.cells[line.id]?.[column.quotationId];
            return (
              <FramedListRow rootAttributes={{ 'data-quote-vendor': column.quotationId }}>
                <List gap="2xs">
                  <Text flush>{column.vendorName}</Text>
                  <Text flush size="compact">
                    Basis: {column.priceBasis ?? '—'}
                  </Text>
                  <Text flush size="compact">
                    Original:{' '}
                    {cell?.unitPrice == null ? (
                      'Not quoted'
                    ) : (
                      <CurrencyAmount value={cell.unitPrice} currency={cell.currency ?? column.currency ?? base} />
                    )}
                  </Text>
                  <Text flush size="compact">
                    Converted:{' '}
                    {cell?.basePrice == null ? '—' : <CurrencyAmount value={cell.basePrice} currency={base} />}
                  </Text>
                </List>
              </FramedListRow>
            );
          })}
        </FramedList>
      ) : (
        <table class="t">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Basis</th>
              <th class="n">Original</th>
              <th class="n">Converted</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((column) => {
              const cell = matrix?.cells[line.id]?.[column.quotationId];
              return (
                <tr data-key={column.quotationId}>
                  <td>{column.vendorName}</td>
                  <td>{column.priceBasis ?? '—'}</td>
                  <td class="n">
                    {cell?.unitPrice == null ? (
                      'Not quoted'
                    ) : (
                      <CurrencyAmount value={cell.unitPrice} currency={cell.currency ?? column.currency ?? base} />
                    )}
                  </td>
                  <td class="n">
                    {cell?.basePrice == null ? '—' : <CurrencyAmount value={cell.basePrice} currency={base} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {reviewFindings.length > 0 ? (
        <List gap="xs" rootAttributes={{ 'data-line-quotation-review': '' }}>
          <ListHeader label="Quotation Review" size="xs" count={reviewFindings.length} countLabel="findings" />
          <FramedList label="Quotation Review">
            {reviewFindings.map((finding) => (
              <FramedListRow
                rootAttributes={{ 'data-review-finding': finding.kind, 'data-severity': finding.severity }}
              >
                <Row vAlign="baseline" gap="xs">
                  <Row flex="none">
                    <Text variant="span" tone={finding.severity === 'high' ? 'danger' : 'quiet'}>
                      <LucideIcon
                        icon={
                          finding.severity === 'high'
                            ? TriangleAlert
                            : finding.severity === 'medium'
                              ? ScanSearch
                              : Info
                        }
                        name=""
                        inline
                      />
                    </Text>
                  </Row>
                  <List flex>
                    <Text flush>{finding.message}</Text>
                    <Text flush tone="quiet" size="compact">
                      {reviewFindingMeta(finding)}
                    </Text>
                  </List>
                </Row>
              </FramedListRow>
            ))}
          </FramedList>
        </List>
      ) : null}
    </List>
  );
}
